import type { Competition } from './interactivity/competition';
import { EntryResponse, fetchEntry } from './interactivity/competition/api';
import { COMP_PASSED_STATES, COMP_QUESTION_TYPES, COMP_ANSWER_CORRECT_TYPES } from './constants';

/**
 * SCORM Definitions
 */

const SCORM_EVENTS = {
  ASSESSMENT: 'assessment',
  PAGE: 'page'
} as const;

/**
 * Assessment Definitions
 */

type CompStatus = 'passed' | 'failed' | 'neutral';
type AnswerCorrectness = 'correct' | 'incorrect' | 'neutral';
type QuestionType = 'choice' | 'fill-in' | 'long-fill-in' | 'other';

interface ScormAssessmentMessage {
  readonly type: 'assessment';
  readonly onlineMagPageCompID: number;
  readonly title: string;
  readonly result: AssessmentResult;
  readonly questions: ReadonlyArray<AssessmentQuestion>;
  readonly comments: string;
}

type AssessmentResult = Readonly<{
  status: CompStatus;
  score: number;
  min: number;
  max: number;
}>;

type AssessmentQuestion = Readonly<{
  id: number;
  prompt: string;
  type: QuestionType;
  actualResponse: number | null;
  comments: string;
  correctResponses: ReadonlyArray<number>;
  result: AnswerCorrectness;
}>;

const toPassed = (passed?: number): CompStatus => {
  switch (passed) {
    case COMP_PASSED_STATES.PASSED:
      return 'passed';
    case COMP_PASSED_STATES.FAILED:
      return 'failed';
    default:
      return 'neutral';
  }
};

const toCorrect = (correct?: number): AnswerCorrectness => {
  switch (correct) {
    case COMP_ANSWER_CORRECT_TYPES.CORRECT:
      return 'correct';
    case COMP_ANSWER_CORRECT_TYPES.INCORRECT:
      return 'incorrect';
    default:
      return 'neutral';
  }
};

const toType = (type?: number): QuestionType => {
  switch (type) {
    case COMP_QUESTION_TYPES.MULTIPLE_CHOICE:
    case COMP_QUESTION_TYPES.IMAGE_CHOICE:
    case COMP_QUESTION_TYPES.TRUE_OR_FALSE:
      return 'choice';
    case COMP_QUESTION_TYPES.FREETEXT_LONG:
      return 'long-fill-in';
    case COMP_QUESTION_TYPES.FREETEXT_SHORT:
      return 'fill-in';
    default:
      return 'other';
  }
};

/**
 * Page Definitions
 */

interface ScormPageMessage {
  readonly type: 'page';
  readonly onlineMagPageID: number;
}

/**
 * Utils
 */

const hasParentWindow = (): boolean =>
  typeof window !== 'undefined' && !!window.parent && window.parent !== window;

const sendError = (err: Error): void => {
  window.errorReporter.sendError({
    col: 0,
    line: 0,
    message: `SCORM: ${err.message}`,
    stack: err.stack,
    type: err.name
  });
};

/**
 * SCORM bridge.
 * If enabled will send messages up to SCORM via the parent window (iFrame).
 */
export class Scorm {
  private readonly _enabled: boolean;

  /**
   * @param enabled Whether SCORM is enabled, I.e., whether View is in "SCORM" mode.
   */
  constructor(enabled: boolean = false) {
    this._enabled = Boolean(enabled);
  }

  /**
   * Fire a page view event into SCORM.
   * This will be sent to the parent window (iFrame) if enabled.
   */
  firePageView(onlineMagPageID: number): void {
    if (!this._enabled || !Number.isFinite(onlineMagPageID) || onlineMagPageID <= 0) {
      return;
    }

    const pageMessage: ScormPageMessage = {
      type: SCORM_EVENTS.PAGE,
      onlineMagPageID
    } as const;

    this.fire(pageMessage);
  }

  /**
   * Fire a comp submitted event into SCORM.
   * This will be sent to the parent window (iFrame) if enabled.
   * The GET /entry endpoint is called first to gather the required data
   *
   * @param comp The comp that was submitted/entered.
   * @param onlineMagPageEntryID The ID of the comp entry in the DB.
   * @returns A promise that resolves when the message has been sent.
   */
  async fireCompSubmitted(comp: Competition, onlineMagPageEntryID: number): Promise<void> {
    if (!this._enabled || !Number.isFinite(onlineMagPageEntryID) || onlineMagPageEntryID <= 0) {
      return;
    }

    return fetchEntry(comp, onlineMagPageEntryID)
      .then((response: EntryResponse) => {
        const assessmentMessage: ScormAssessmentMessage = {
          type: SCORM_EVENTS.ASSESSMENT,
          onlineMagPageCompID: response.onlineMagPageCompID,
          title: response.onlineMagPageCompTitle,
          result: {
            status: toPassed(response.result.passed),
            score: response.result.score,
            min: response.result.min,
            max: response.result.max
          },
          questions: response.questions.map(
            (q): AssessmentQuestion => ({
              id: q.id,
              prompt: q.prompt,
              type: toType(q.type),
              actualResponse: q.selectedAnswerID,
              comments: q.comments,
              correctResponses: q.correctAnswerIDs,
              result: toCorrect(q.correct)
            })
          ),
          comments: response.comments
        } as const;

        this.fire(assessmentMessage);
      })
      .catch(error => {
        const isError = error instanceof Error;

        isError && sendError(error);
      });
  }

  private fire(message: any): void {
    if (!this._enabled || !hasParentWindow()) {
      return;
    }

    try {
      window.parent!.postMessage(message, '*');
    } catch (error) {
      const isError = error instanceof Error;

      isError && sendError(error);
    }
  }
}
